import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface Props {
  /** The explanatory text shown in the floating panel (also the trigger's aria-label). */
  text: string;
  /** Preferred side; auto-flips to bottom when too close to the top edge. */
  side?: 'top' | 'bottom';
  /** Override the default small info-icon trigger. */
  trigger?: ReactNode;
  /** Fires alongside the trigger's pin-toggle click — e.g. the credits pill's "request more" mailto. */
  onClick?: () => void;
  /**
   * A hover-only trigger, for a tip inside an element that is itself clickable (the Calendar day cell's
   * events banner, 2026-09-26): a plain `<span>` with no tab stop, no click-to-pin and no aria-label, so
   * the parent keeps its click, its keyboard access and its accessible name. A click closes the tip and
   * reaches the parent. The panel is the same.
   */
  hoverOnly?: boolean;
  /** Keeps the line breaks in `text` (one item per line). */
  multiline?: boolean;
  /**
   * Rich panel content, rendered instead of `text` (2026-09-27, the Calendar's economic-events tooltip, drawn
   * with the app's own elements). `text` stays required: it is the plain-text equivalent (and, on a button
   * trigger, the accessible name).
   */
  content?: ReactNode;
  /** A wider panel (320px instead of 240px), for rich content with rows. */
  wide?: boolean;
  className?: string;
}

const GAP = 8;

interface Pos { left: number; top: number; placement: 'top' | 'bottom'; }

/**
 * Accessible info tooltip. A small (i) trigger reveals a token-styled panel on **hover, focus, and
 * click** — the click "pins" it so it also works on touch (tap to toggle). Closes on Escape,
 * outside-click, blur, and scroll. The panel is portaled to <body> so it escapes card `overflow`,
 * and is `pointer-events-none` so it never traps the cursor. All colours are theme variables, so it
 * reads correctly in dark + light.
 */
export function Tooltip({ text, side = 'top', trigger, onClick, hoverOnly = false, multiline = false, content, wide = false, className = '' }: Props) {
  const [open, setOpen] = useState(false);
  const pinnedRef = useRef(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const setTrigger = (el: HTMLElement | null) => { triggerRef.current = el; };
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  /**
   * Horizontal correction, in px, that keeps the panel inside the viewport (2026-09-02 fix: "tooltip
   * too narrowed"). The panel is `position: fixed` and centred on the trigger with `translateX(-50%)`;
   * a fixed box shrinks to fit the space between its `left` and the viewport's right edge, so a
   * trigger near that edge (the CREDIT COST ⓘ at the foot of the 400px panel) left it a few dozen
   * pixels of width and it wrapped one word per line. `width: max-content` below stops the shrink;
   * this shift then slides the full-width panel back inside the viewport instead of off it.
   */
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    if (!open || !pos) { setShift(0); return; }
    const r = panelRef.current?.getBoundingClientRect();
    if (!r) return;
    const overflowRight = r.right - (window.innerWidth - GAP);
    const overflowLeft = GAP - r.left;
    if (overflowRight > 0) setShift(-overflowRight);
    else if (overflowLeft > 0) setShift(overflowLeft);
    else setShift(0);
  }, [open, pos]);

  const measure = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const placement: 'top' | 'bottom' = side === 'top' ? (r.top > 140 ? 'top' : 'bottom') : 'bottom';
    setPos({
      left: r.left + r.width / 2,
      top: placement === 'top' ? r.top - GAP : r.bottom + GAP,
      placement,
    });
  };

  const show = () => { measure(); setOpen(true); };
  const hideUnlessPinned = () => { if (!pinnedRef.current) setOpen(false); };
  const close = () => { pinnedRef.current = false; setOpen(false); };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => close();
    // An Escape a `Popover` already took (2026-10-14) closes that layer only.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) close(); };
    const onClickAway = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickAway);
    };
  }, [open]);

  return (
    <>
      {hoverOnly ? (
        <span ref={setTrigger} onMouseEnter={show} onMouseLeave={close} onMouseDown={close} className={className}>
          {trigger}
        </span>
      ) : (
        <button
          ref={setTrigger}
          type="button"
          aria-label={text}
          onMouseEnter={show}
          onMouseLeave={hideUnlessPinned}
          onFocus={show}
          onBlur={close}
          onClick={() => {
            // Tap/click toggles a pinned state so the tip survives on touch devices.
            if (pinnedRef.current) { close(); }
            else { pinnedRef.current = true; show(); }
            onClick?.();
          }}
          className={`inline-flex items-center justify-center rounded text-text-subtle transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${className}`}
        >
          {trigger ?? <Info className="h-3 w-3" />}
        </button>
      )}
      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="tooltip"
          className={`pointer-events-none fixed z-[60] ${wide ? 'max-w-[320px]' : 'max-w-[240px]'} rounded-lg border px-3 py-2 text-xs leading-snug shadow-card ${multiline ? 'whitespace-pre-line' : ''}`}
          style={{
            left: pos.left,
            top: pos.top,
            // `max-content`: a fixed element otherwise shrink-wraps to the room left of the viewport
            // edge, which is what squeezed the panel to a word per line (see `shift`).
            width: 'max-content',
            transform: `translate(calc(-50% + ${shift}px), ${pos.placement === 'top' ? '-100%' : '0'})`,
            background: 'var(--canvas-raised)',
            color: 'var(--text)',
            borderColor: 'var(--canvas-divider)',
          }}
        >
          {content ?? text}
        </div>,
        document.body,
      )}
    </>
  );
}
